/**
 * The Members table: rows, filters and the chip counts above it.
 *
 * Pure, so the filtering and the counts can be tested without rendering.
 * Spec: `vMembers()` in docs/design/netly-pay/prototype.html, DESIGN-BRIEF.md §C.
 */
import {
  batchOf,
  digitsOf,
  fmtDate,
  lastPayment,
  memberBalance,
  memberStatus,
  money,
  programOf,
  todayISO,
  type Member,
  type MemberStatus,
  type NetlyPayData,
} from '../../internals/managers/NetlyPayManager';

export interface MemberFilters {
  q: string;
  programId: string;
  batchId: string;
  status: string;
}

export const NO_FILTERS: MemberFilters = {q: '', programId: 'all', batchId: 'all', status: 'all'};

export interface MemberRow {
  id: string;
  name: string;
  /** The first guardian, or "Adult member" — the brief's wording. */
  guardian: string;
  phone: string;
  group: string;
  status: MemberStatus;
  balance: string;
  /** Owed money reads in the danger colour only when the member is overdue. */
  balanceTone: 'default' | 'danger' | 'muted';
  lastPayment: string;
  initials: string;
}

/** Two initials from a name, for the row avatar. */
export function initialsOf(name: string): string {
  const parts = String(name ?? '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return '–';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Does this member match the search box?
 *
 * Name, member number and phone. The phone is matched on digits, so a search
 * typed as "077 123" finds a number stored as "+94771234501"; a search with
 * no digits in it never matches a phone, which stops the letter "a" matching
 * every member through an empty digit string.
 */
export function matchesQuery(member: Member, q: string): boolean {
  const needle = q.trim().toLowerCase();
  if (!needle) return true;
  if (member.name.toLowerCase().includes(needle)) return true;
  if (member.id.toLowerCase().includes(needle)) return true;
  const digits = digitsOf(needle);
  return digits.length > 0 && digitsOf(member.phone).includes(digits);
}

export interface MemberTable {
  rows: MemberRow[];
  /** Counts for the filter chips, over ALL members, not the filtered set. */
  counts: Record<string, number>;
  summary: string;
}

export function memberTable(
  data: NetlyPayData,
  filters: MemberFilters = NO_FILTERS,
  today: string = todayISO(),
  currency = 'Rs.',
): MemberTable {
  const members = data?.members ?? [];
  const withStatus = members.map((m) => ({member: m, status: memberStatus(data, m, today)}));

  // Counts are of everyone. A chip that said "Overdue 0" because the current
  // filter hides them would be a lie about the business.
  const counts: Record<string, number> = {all: withStatus.length};
  withStatus.forEach(({status}) => {
    counts[status] = (counts[status] ?? 0) + 1;
  });

  const rows = withStatus
    .filter(({member, status}) => {
      if (!matchesQuery(member, filters.q)) return false;
      if (filters.programId !== 'all') {
        const batch = batchOf(data, member.batchId);
        if (batch?.programId !== filters.programId) return false;
      }
      if (filters.batchId !== 'all' && member.batchId !== filters.batchId) return false;
      if (filters.status !== 'all' && status !== filters.status) return false;
      return true;
    })
    .map(({member, status}) => {
      const batch = batchOf(data, member.batchId);
      const program = programOf(data, member.batchId);
      const balance = memberBalance(data, member);
      const last = lastPayment(data, member);
      const guardian = member.guardians?.[0];

      return {
        id: member.id,
        name: member.name,
        guardian: guardian ? `${guardian.name} · ${guardian.relation}` : 'Adult member',
        phone: member.phone,
        group: [program?.name, batch?.name].filter(Boolean).join(' · ') || '—',
        status,
        balance: money(balance, currency),
        balanceTone: balance > 0 ? (status === 'overdue' ? 'danger' : 'default') : 'muted',
        lastPayment: last ? fmtDate(last.date) : '—',
        initials: initialsOf(member.name),
      } as MemberRow;
    });

  const overdue = counts.overdue ?? 0;
  const pending = counts.pending ?? 0;
  const summary = `${members.length} members · ${overdue} overdue · ${pending} waiting to be approved`;

  return {rows, counts, summary};
}

/** The filter chips, in the brief's order. */
export const STATUS_FILTERS: {key: string; label: string}[] = [
  {key: 'all', label: 'All'},
  {key: 'active', label: 'Active'},
  {key: 'overdue', label: 'Overdue'},
  {key: 'frozen', label: 'Frozen'},
  {key: 'expired', label: 'Expired'},
  {key: 'pending', label: 'Pending approval'},
];

/** What the bulk bar says once rows are ticked. */
export function selectionSummary(
  data: NetlyPayData,
  ids: string[],
  currency = 'Rs.',
): string {
  const members = (data?.members ?? []).filter((m) => ids.includes(m.id));
  const balance = members.reduce((s, m) => s + memberBalance(data, m), 0);
  const label = members.length === 1 ? 'member selected' : 'members selected';
  return `${members.length} ${label} · balance ${money(balance, currency)}`;
}
